<template>
  <div class="login-container">
    <n-card title="OnePlaceHQ Admin" class="login-card">
      <n-form ref="formRef" :model="form" :rules="rules">
        <n-form-item label="Phone / Email" path="username">
          <n-input v-model:value="form.username" placeholder="Enter 10-digit phone or email" />
        </n-form-item>
        <n-form-item label="Password" path="password">
          <n-input
            v-model:value="form.password"
            type="password"
            placeholder="Enter password"
            show-password-on="click"
            @keyup.enter="handleLogin"
          />
        </n-form-item>
        <n-button
          type="primary"
          block
          :loading="loading"
          @click="handleLogin"
        >
          Login
        </n-button>
      </n-form>
      <n-alert v-if="error" type="error" class="error-alert">
        {{ error }}
      </n-alert>
    </n-card>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { NCard, NForm, NFormItem, NInput, NButton, NAlert } from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { AUTH_URL, ADMIN_URL } from '../config/api'

const router = useRouter()
const toast = useToast()

const formRef = ref(null)
const loading = ref(false)
const error = ref(null)

const form = ref({
  username: '',
  password: '',
})

const rules = {
  username: [
    { required: true, message: 'Phone or email is required', trigger: 'blur' },
    {
      validator: (rule, value) => {
        if (!value) return true
        const isPhone = /^\d{10}$/.test(value)
        const isEmail = /^[\w\-\.]+@([\w\-]+\.)+[\w\-]{2,4}$/.test(value)
        return isPhone || isEmail
      },
      message: 'Enter a valid 10-digit phone or email address',
      trigger: 'blur',
    },
  ],
  password: { required: true, message: 'Password is required', trigger: 'blur' },
}

const handleLogin = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await fetch(`${AUTH_URL}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form.value),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.detail || data.error || 'Login failed')
    }

    let token = data.data.token

    // Handle multi-profile users: auto-select first super-admin context
    if (data.data.requires_selection && data.data.contexts) {
      const selectionToken = data.data.selection_token
      // Select the first context (super admin profiles are already validated server-side)
      const firstContext = data.data.contexts[0]
      const selectResponse = await fetch(`${AUTH_URL}/select-context`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selection_token: selectionToken,
          profile_id: firstContext.profile_id,
        }),
      })

      const selectData = await selectResponse.json()
      if (!selectResponse.ok) {
        throw new Error(selectData.detail || 'Context selection failed')
      }
      token = selectData.data.token
    }

    if (!token) {
      throw new Error('No authentication token received')
    }

    // Check if user is super admin by making a test request
    localStorage.setItem('auth_token', token)

    // Test admin access
    const testResponse = await fetch(`${ADMIN_URL}/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    })

    if (!testResponse.ok) {
      localStorage.removeItem('auth_token')
      throw new Error('Super admin access required')
    }

    toast.success('Login successful')
    router.push('/')
  } catch (e) {
    error.value = e.message
    localStorage.removeItem('auth_token')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.login-card {
  width: 100%;
  max-width: 400px;
}

.error-alert {
  margin-top: 16px;
}
</style>
